import React, { useState, useEffect } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import {
  Zap, User, MapPin, Star, Clock, CheckCircle, Loader2,
  TrendingUp, AlertCircle, Truck
} from "lucide-react";

const AutoAssignDialog = ({ booking, isOpen, onClose, onAssigned }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  useEffect(() => {
    if (isOpen && booking) {
      fetchSuggestions();
    }
  }, [isOpen, booking]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/auto-assign/suggestions/${booking.id}`);
      setSuggestions(response.data);
    } catch (error) {
      toast.error("Failed to get driver suggestions");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    setAssigning(true);
    try {
      const response = await api.post(`/auto-assign/assign/${booking.id}`);
      toast.success(`Assigned to ${response.data.driver_name}!`);
      onAssigned?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to auto-assign");
    } finally {
      setAssigning(false);
    }
  };

  const handleManualAssign = async (driverId) => {
    setAssigning(true);
    setSelectedDriverId(driverId);
    try {
      await api.post(`/bookings/${booking.id}/dispatch`, {
        driver_id: driverId,
        send_to_calendar: false
      });
      toast.success("Driver assigned!");
      onAssigned?.();
      onClose();
    } catch (error) {
      toast.error("Failed to assign driver");
    } finally {
      setAssigning(false);
      setSelectedDriverId(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-yellow-400";
    if (score >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return "bg-green-500/15";
    if (score >= 60) return "bg-yellow-500/15";
    if (score >= 40) return "bg-orange-500/15";
    return "bg-red-500/15";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#C8A95B]" />
            Smart Driver Assignment
          </DialogTitle>
        </DialogHeader>

        {booking && (
          <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] mb-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[#C8A95B]">{booking.booking_ref}</span>
              <span className="text-sm">{booking.passenger_name}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{booking.pickup_location}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {new Date(booking.pickup_datetime).toLocaleString()}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No drivers available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map((driver, index) => (
                <div
                  key={driver.driver_id}
                  className={`p-3 rounded-xl border transition-all ${
                    driver.is_available && driver.availability_status === "online"
                      ? "border-green-500/20 bg-green-500/[0.02] hover:border-green-500/40"
                      : driver.is_available
                      ? "border-white/[0.06] hover:border-white/[0.12]"
                      : "border-white/[0.04] bg-white/[0.01] opacity-60"
                  }`}
                  data-testid={`suggestion-${driver.driver_id}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 && driver.is_available ? "bg-[#C8A95B] text-black" : "bg-white/[0.04] text-muted-foreground"
                    }`}>
                      {index + 1}
                    </div>

                    {/* Driver Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{driver.driver_name}</span>
                        <Badge className={`${
                          driver.availability_status === "online" ? "bg-green-500/15 text-green-400" :
                          driver.availability_status === "on_job" ? "bg-blue-500/15 text-blue-400" :
                          "bg-gray-500/15 text-gray-400"
                        } text-[10px] border-0`}>
                          {driver.availability_status}
                        </Badge>
                        {!driver.is_available && (
                          <Badge className="bg-red-500/15 text-red-400 text-[10px] border-0">Unavailable</Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {driver.distance_km && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {driver.distance_km} km
                          </span>
                        )}
                        {driver.estimated_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            ~{driver.estimated_minutes} min
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Truck className="h-3 w-3" />
                          {driver.completed_today} today
                        </span>
                        {driver.average_rating && (
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-400" />
                            {driver.average_rating.toFixed(1)}
                          </span>
                        )}
                      </div>

                      {/* Reasons */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {driver.reasons.slice(0, 3).map((reason, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-muted-foreground">
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Score & Action */}
                    <div className="flex flex-col items-end gap-2">
                      <div className={`px-3 py-1 rounded-full ${getScoreBgColor(driver.score)}`}>
                        <span className={`text-sm font-bold ${getScoreColor(driver.score)}`}>
                          {driver.score.toFixed(0)}
                        </span>
                      </div>
                      
                      {driver.is_available && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          disabled={assigning}
                          onClick={() => handleManualAssign(driver.driver_id)}
                        >
                          {assigning && selectedDriverId === driver.driver_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Assign
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleAutoAssign}
            disabled={assigning || suggestions.filter(s => s.is_available).length === 0}
            className="bg-[#C8A95B] text-black hover:bg-[#d4b96a]"
            data-testid="auto-assign-btn"
          >
            {assigning ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Auto-Assign Best
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoAssignDialog;
